import * as React from 'react';
import { format, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import { Calendar, type CalendarProps } from '@/shared/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';

interface DatePickerProps {
  date?: Date;
  onSelect: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minDate?: Date;
  disabledDays?: CalendarProps['disabled'];
}

export function DatePicker({
  date,
  onSelect,
  placeholder = 'Selecione uma data',
  disabled = false,
  className,
  minDate,
  disabledDays,
}: DatePickerProps) {
  const minDateMatcher = React.useMemo(
    () => (minDate ? { before: startOfDay(minDate) } : undefined),
    [minDate]
  );

  const mergedDisabled = React.useMemo(() => {
    if (minDateMatcher && disabledDays) {
      if (Array.isArray(disabledDays)) {
        return [minDateMatcher, ...disabledDays];
      }
      return [minDateMatcher, disabledDays];
    }
    return minDateMatcher ?? disabledDays;
  }, [disabledDays, minDateMatcher]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal',
            !date && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onSelect}
          initialFocus
          locale={ptBR}
          disabled={mergedDisabled}
        />
      </PopoverContent>
    </Popover>
  );
}
